import * as _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

import {
  ActionsPopOut,
  IStep
} from "src/library/components/ActionsPopOut/index";
import { Button } from "src/library/components/Button";
import { OrderDayPicker } from "src/library/components/DayPicker";
import { Icon } from "src/library/components/Icon";
import { Modal } from "src/library/components/Modal";
import {
  hasOrders,
  isDayValid
} from "src/library/utils/date-helpers";
import { useEventListener } from "src/library/hooks/useEventListener";
import { useWindowSize } from "src/library/hooks/useWindowSize";
import { ButtonType } from "src/library/components/Button/Button";
import { Icons } from "src/library/components/Icon/types";
import {
  getOrderMode,
  getSingleOrder
} from "src/core/drops/utils/inherited-settings";
import type { IStore } from "src/core/shared/store";
import { useOrderingAvailableForDay } from "src/core/orders/hooks/validation";
import useMinimumLeadTime from "src/core/shared/hooks/useMinimumLeadTime";
import {
  IProduct,
  ProductStatus
} from "src/core/products/api/products.api";
import {
  IDrop,
  IOrderModeSetting
} from "src/core/drops/api/drops.api";
import { useDisabledDeliveryDate } from "src/core/shared/hooks/useDisabledDeliveryDate";
import { useWarningDeliveryDates } from "src/core/shared/hooks/useWarningDeliveryDate";
import { ConfirmMultipleOrderModal } from "src/core/orders/components/Modals";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { useOrderDeliveryDates } from "src/core/orders/hooks/useOrderDeliveryDates";
import { IOrder } from "src/core/orders/api/orders.api";
import SelectedDrop from "src/core/drops/components/SelectedDrop";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { detect } = require("detect-browser");

interface IOrderContextProps {
  order?: IOrder | null;
  createDraftOrder: (deliveryDate: Date | null, drop?: IDrop | null) => void;
  loading: boolean;
  product?: IProduct;
  drop: IDrop | null;
  direction: "right" | "left" | "overlay";
  isHeader?: boolean;
  $buttonType?: ButtonType;
  icon?: boolean;
  requireDropSelection?: boolean
  onOrderModalChange?: (isOpen: boolean) => void;
}

export const CreateOrderContext: React.ReactFCWithChildren<IOrderContextProps> = ({
  createDraftOrder,
  product,
  loading,
  drop,
  direction,
  onOrderModalChange,
  isHeader = false,
  $buttonType = "primary",
  icon = true,
  order,
  requireDropSelection = false
}) => {
  const { terminology, installedModules } = useMappedState(({ global }: IStore) => ({
    terminology: global.settings?.terminology,
    installedModules: global.settings?.installedModules || []
  }));

  const [ position, setPosition ] = React.useState({
    x: 0,
    y: 0
  });

  // only used in the admin drop selection step
  const [ manuallySelectedDrop, setManuallySelectedDrop ] = React.useState<IDrop | null>(null);

  const selectedDrop = React.useMemo(() => {
    return manuallySelectedDrop || drop;
  }, [ drop, manuallySelectedDrop ]);

  const orderingNotAvailableForDay = useOrderingAvailableForDay(selectedDrop);
  const browser = detect();
  const { minimumLeadTime } = useMinimumLeadTime();
  const divRef = React.useRef<HTMLDivElement>(null);
  const history = useHistory();
  const [ modalData, setModalData ] = React.useState<string | null>(null);
  const [ showMultipleOrderModal, setShowMultipleOrderModal ] = React.useState(false);
  const [ { orderDates, currentOrders }, { getCalendarOrderDates } ] = useOrderDeliveryDates();
  const singleDailyDelivery = selectedDrop && getSingleOrder(selectedDrop.groupedSettings);
  const orderMode = selectedDrop ? getOrderMode(selectedDrop.groupedSettings) : null;
  const [ showAction, setShowAction ] = React.useState(false);
  const [ orderDate, setOrderDate ] = React.useState<Date | null>(null);
  const [ hasOrderOnDay, setHasOrderOnDay ] = React.useState<boolean>(false);
  const [ selectedOrderMode, setSelectedOrderMode ] = React.useState<IOrderModeSetting["allow"] | null>(requireDropSelection ? "DL" : orderMode ? orderMode.allow : null);
  const isDisabled = useDisabledDeliveryDate(selectedDrop);
  const isWarning = useWarningDeliveryDates();
  const canCreateOrderPermission = useCheckUserPermission();

  const minimumDate = React.useMemo(() => {
    return moment.utc().add(minimumLeadTime?.defaultLeadTimeDays || 0, "days").startOf("day").toDate();
  }, [ minimumLeadTime ]);

  const [ currentMonth, setCurrentMonth ] = React.useState<{
    month: number;
    year: number;
  }>({
    month: minimumDate.getMonth(),
    year: minimumDate.getFullYear()
  });

  const windowSize = useWindowSize();
  const [ isCollapsed, setIsCollapsed ] = React.useState(false);

  React.useEffect(() => {
    if (drop?.id && showAction) {
      getCalendarOrderDates(drop?.id, currentMonth);
    }
  }, [
    currentMonth,
    showAction,
    getCalendarOrderDates,
    drop?.id
  ]);

  React.useEffect(() => {
    if (order) {
      setShowAction(true);
    }
  }, [ order ]);

  React.useEffect(() => {
    if (windowSize.width) {
      if (windowSize.width < 1024) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    }
  }, [ windowSize.width ]);

  const toggleShowAction = React.useCallback(() => {
    if (requireDropSelection) {
      setShowAction(currShowAction => !currShowAction);
    } else if (orderMode) {
      // Don't create dateless draft order if RJL installed -
      // We don't want to link RJL delivery numbers to orders without a date set
      if ((orderMode.allow === "MTO" || product) && !installedModules?.includes("rjl")) {
        // Jump straight into order
        createDraftOrder(null);
      } else {
        // Open up action dropdown for user:
        setShowAction(currShowAction => !currShowAction);
        setSelectedOrderMode(orderMode.allow);
      }
    }
  }, [
    createDraftOrder,
    orderMode,
    product,
    requireDropSelection,
    installedModules
  ]);

  React.useEffect(() => {
    if (showAction) {
      setOverlayPosition(true);
    }

    if (onOrderModalChange) {
      onOrderModalChange(showAction);
    }
  }, [ showAction ]);

  const setOverlayPosition = React.useCallback((autoScroll = false) => {
    if (divRef.current) {
      const bounds = divRef.current.getBoundingClientRect();
      let right = bounds.right - bounds.width;

      if (direction === "overlay") {
        setPosition({
          y: bounds.top - 378,
          x: bounds.right - 400
        });

        return;
      }

      if (window.outerWidth <= 500) {
        right = 0;
      } else if (bounds.right + 400 > window.outerWidth) {
        right = bounds.right - 310;
      }

      setPosition({
        y: bounds.top + bounds.height,
        x: right
      });

      if (autoScroll && browser.os !== "iOS") {
        const scrollWrapper = document.getElementById("main-wrapper");

        if (scrollWrapper) {
          const currentBottom = bounds.top + 350;
          const currentScroll = scrollWrapper.scrollTop;

          if (currentBottom > window.outerHeight) {
            scrollWrapper.scrollTo({
              top: currentScroll + (currentBottom - window.outerHeight),
              behavior: "smooth"
            });
          }
        }
      }
    }
  }, [ browser.os, direction ]);

  const handler = React.useCallback(() => {
    setOverlayPosition();
  }, [ setOverlayPosition ]);

  useEventListener("resize", handler);
  useEventListener("scroll", handler, document.getElementById("main-wrapper"));
  useEventListener("scroll", handler, document.getElementsByClassName("rt-table")[ 0 ]);

  const dateLedOrderingDayPicker: IStep = React.useMemo(
    () => ({
      title: "Choose your delivery date",
      component: (
        <OrderDayPicker
          singleDailyDelivery={singleDailyDelivery ? singleDailyDelivery.value : false}
          orderDates={orderDates}
          date={minimumDate}
          handleDateChange={day => {
            if (isDayValid(day, minimumDate, []) || orderingNotAvailableForDay(day, product) !== null) {
              setOrderDate(day);
              setHasOrderOnDay(hasOrders(day, orderDates));
            } else {
              setOrderDate(null);
            }
          }}
          handleMonthChange={(month: Date) => {
            setCurrentMonth({
              month: month.getMonth(),
              year: month.getFullYear()
            });
          }}
          disabled={date => isDisabled(date, currentOrders, product)}
          fromDate={minimumDate}
          warning={date => isWarning(date, product)}
          toDate={moment
            .utc()
            .add(2, "years")
            .toDate()}
        />
      ),
      allowNext: orderDate !== null && orderingNotAvailableForDay(orderDate, product) === null
    }),
    [
      singleDailyDelivery,
      orderDates,
      minimumDate,
      isWarning,
      orderDate,
      orderingNotAvailableForDay,
      product,
      isDisabled,
      currentOrders
    ]
  );

  const dropSelectionStep = React.useMemo(() => {
    if (requireDropSelection) {
      const dropSelectionStep: IStep = {
        title: `Select ${terminology?.drop.singular.text}`,
        allowNext: true,
        hideFooter: false,
        component: (
          <SelectDropWrapper>
            <SelectedDrop
              drop={manuallySelectedDrop || null}
              handleUpdate={newDrop => {
                setManuallySelectedDrop(newDrop);
              }}
              terminology={terminology}
              placeHolder={`Please Select ${_.startCase(terminology?.drop.singular.text)}`}
            />
          </SelectDropWrapper>
        )
      };

      return dropSelectionStep;
    }

    return null;
  }, [
    manuallySelectedDrop,
    requireDropSelection,
    terminology
  ]);

  const createOrderSteps = React.useMemo(() => {
    let steps: IStep[] = [];

    /**
     * Switch to handle what kind of action dropdown
     * should be displayed. If any, the user needs to choose
     * either DL (and show a calendar) or MTO (and jump
     * straight into the draft order)
     */
    switch (selectedOrderMode) {
      case "ANY":
        steps = [
          {
            title: "How would you like to order?",
            component: (
              <OrderModeAnyWrapper>
                <Button onClick={() => setSelectedOrderMode("DL")}>
                  Select delivery date first
                </Button>

                <Button onClick={() => createDraftOrder(null)}>
                  Select products first
                </Button>
              </OrderModeAnyWrapper>
            ),
            allowNext: true,
            hideFooter: false
          }
        ];
        break;
      case "DL":
        steps = [ dateLedOrderingDayPicker ];
        break;
      default:
        steps = [ dateLedOrderingDayPicker ];
        break;
    }

    if (dropSelectionStep) {
      steps.unshift(dropSelectionStep);
    }

    return steps;
  }, [
    selectedOrderMode,
    dropSelectionStep,
    dateLedOrderingDayPicker,
    createDraftOrder
  ]);

  const handleCreateOrderAction = React.useCallback(() => {
    if (orderDate) {
      createDraftOrder(orderDate, selectedDrop);
      toggleShowAction();
    }
  }, [
    createDraftOrder,
    orderDate,
    selectedDrop,
    toggleShowAction
  ]);

  const actionsPopOutAction = React.useCallback(() => {
    if (orderDate) {
      // If one-order-per-day setting is true, and an order exists:
      if (singleDailyDelivery && singleDailyDelivery.value === true && hasOrderOnDay) {
        const orderDay = orderDates.find(or => moment.utc(or.date).isSame(orderDate, "day"));

        if (orderDay) {
          setModalData(`/orders/${orderDay.id}`);
        } else {
          setModalData("/orders");
        }
      } else if (hasOrderOnDay) {
        setShowMultipleOrderModal(true);
      } else {
        handleCreateOrderAction();
      }
    }
  }, [
    handleCreateOrderAction,
    hasOrderOnDay,
    orderDate,
    orderDates,
    singleDailyDelivery
  ]);

  return (
    <>
      <OrderContextWrapper>
        {isCollapsed && isHeader && icon ? (
          <Icon
            icon={Icons.CART}
            onClick={toggleShowAction}
          />
        ) : product ? (
          <Button
            $buttonType={$buttonType}
            icon={icon ? Icons.CART : undefined}
            onClick={() => toggleShowAction()}
            size="medium"
            loading={loading}
            disabled={product.currentStatus?.status === ProductStatus.Suspended || product.currentStatus?.status === ProductStatus.Delisted || !product.prices?.length}
          >
            {`Add to ${terminology?.cart?.singular.text || "cart"}`}
          </Button>
        ) : (
          <Button
            icon={Icons.CALENDAR}
            $buttonType={$buttonType}
            onClick={toggleShowAction}
            size="medium"
            loading={loading}
            disabled={!canCreateOrderPermission("create-order")}
          >
            Create order
          </Button>
        )}

        {!!showAction && (
          <ActionsPopOut
            directionHorizontal="left"
            directionVertical="top"
            position={position}
            steps={createOrderSteps}
            ref={divRef}
            disableAction={!canCreateOrderPermission("create-order")}
            hideCaret={direction === "overlay"}
            action={() => actionsPopOutAction()}
            handleClose={toggleShowAction}
            className="orders-popout"
          />
        )}
      </OrderContextWrapper>

      <Modal
        small
        actions={[
          {
            name: "Go back",
            action: () => setModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "Edit existing order",
            action: () => (modalData !== null ? history.push(modalData, { edit: true }) : null),
            $buttonType: "primary"
          }
        ]}
        backgroundAction={() => setModalData(null)}
        body="If you want to order more products for this date, you must edit the existing order for this date."
        type="confirm"
        title="You already have an order for this date"
        show={modalData !== null}
      />

      {orderDate && (
        <ConfirmMultipleOrderModal
          onDismiss={() => setShowMultipleOrderModal(false)}
          open={showMultipleOrderModal}
          onConfirm={() => handleCreateOrderAction()}
          orderDates={orderDates}
          selectedDay={orderDate}
          terminology={terminology}
          ordersLoaded={!!currentOrders}
        />
      )}
    </>
  );
};

const OrderModeAnyWrapper = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.padding.md};
  flex-direction: column;
  padding-top: 0;
  > * {
    margin-top: ${({ theme }) => theme.spacing.padding.sm};
  }
`;

const SelectDropWrapper = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.padding.md};
  flex-direction: column;
  padding-top: 0;
  > * {
    margin-top: ${({ theme }) => theme.spacing.padding.sm};
  }
`;

const OrderContextWrapper = styled.div`
  position: "relative";
`;
